const ICD_CODE_REGEX = /^[A-Z]\d{2}(?:\.\d{1,3})?$/i;

/** Matches ScribeAI / allPrompts SOAP ICD heading styles */
export const ICD_HEADING_STYLE = {
  ASSESSMENT_BULLET: "assessment-bullet", // - **Name (CODE):**
  INLINE_BOLD: "inline-bold", // **Name (CODE):**
  NUMBERED: "numbered", // 1. **Name (CODE):**
};

export function isValidIcd10Code(code) {
  return ICD_CODE_REGEX.test(String(code || "").trim());
}

export function normalizeIcd10Code(code) {
  return String(code || "").trim().toUpperCase();
}

function escapeRegex(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Short clinical label for diagnosis headings from ICD description. */
export function formatHeadingFromDescription(description = "") {
  let label = String(description || "").trim();
  if (!label) return "Diagnosis";
  if (label.length > 80) {
    const shortened = label.split(/[;,]/)[0]?.trim();
    label = shortened && shortened.length > 0 ? shortened : label;
  }
  if (label.length > 80) label = `${label.slice(0, 77)}...`;
  return label;
}

/**
 * Detect how existing SOAP text formats ICD diagnosis headings.
 * Default: assessment-bullet per allPrompts / ScribeAI integrated A/P.
 */
export function detectIcdHeadingStyle(text = "") {
  const sample = String(text || "");
  if (/^\s*\d+\.\s*\*\*[^*]+\([A-Z]\d/i.test(sample)) {
    return ICD_HEADING_STYLE.NUMBERED;
  }
  if (
    /^\s*-\s*\*\*[^*]+\([A-Z]\d/i.test(sample) ||
    /\n\s*-\s*\*\*[^*]+\([A-Z]\d/i.test(sample)
  ) {
    return ICD_HEADING_STYLE.ASSESSMENT_BULLET;
  }
  if (/\*\*[^*]+\([A-Z]\d[^)]*\)\s*\*\*/i.test(sample)) {
    return ICD_HEADING_STYLE.INLINE_BOLD;
  }
  return ICD_HEADING_STYLE.ASSESSMENT_BULLET;
}

/**
 * Canonical diagnosis heading line(s) per generation prompts / backend normalize:
 * **Condition Name (ICD-10 code):** with optional leading "-" or "1."
 * Colon stays inside bold so formatAsParagraphs can mark headings clickable.
 */
export function buildIcdDiagnosisHeadingLine(
  description,
  code,
  style = ICD_HEADING_STYLE.ASSESSMENT_BULLET,
  { lineNumber } = {},
) {
  const label = formatHeadingFromDescription(description);
  const c = normalizeIcd10Code(code);
  const inner = `**${label} (${c}):**`;

  switch (style) {
    case ICD_HEADING_STYLE.NUMBERED:
      return `${lineNumber ?? 1}. ${inner}`;
    case ICD_HEADING_STYLE.ASSESSMENT_BULLET:
      return `- ${inner}`;
    case ICD_HEADING_STYLE.INLINE_BOLD:
    default:
      return inner;
  }
}

/**
 * ScribeAI / backend canonical heading: **Condition (CODE):**
 * Also matches bullet / numbered / colon-outside variants used before normalization.
 */
export function isIcdDiagnosisHeadingLine(line = "") {
  const trimmed = String(line || "").trim();
  if (!trimmed) return false;
  if (/^(?:\*\*)?\s*plan\s*:/i.test(trimmed)) return false;
  return (
    // **Label (CODE):** (colon inside bold — canonical)
    /^\*\*.+\(\s*[A-Z][A-Z0-9.]*\s*\)\s*:?\s*\*\*\s*:?\s*$/i.test(trimmed) ||
    // - **Label (CODE):** / - **Label (CODE)**: / - **Label (CODE)**
    /^\s*[-*•]\s+\*\*.+\(\s*[A-Z][A-Z0-9.]*\s*\)\s*:?\s*\*\*\s*:?\s*$/i.test(
      trimmed,
    ) ||
    // 1. **Label (CODE):**
    /^\s*\d+\.\s*\*\*.+\(\s*[A-Z][A-Z0-9.]*\s*\)\s*:?\s*\*\*\s*:?\s*$/i.test(
      trimmed,
    ) ||
    /^\s*[-*•]?\s*[A-Z][0-9][A-Z0-9.]*\s*[–-]\s*.+$/i.test(trimmed) ||
    /^\s*[-*•]?\s*\*\*[A-Z][0-9][A-Z0-9.]*\s*[–-]\s*.+\*\*\s*:?\s*$/i.test(
      trimmed,
    )
  );
}

/** Split text into editable blocks at each ICD diagnosis heading. */
export function splitTextByDiagnosisHeadings(text = "") {
  const normalized = String(text || "").replace(/\r\n/g, "\n").trim();
  if (!normalized) return [];

  const lines = normalized.split("\n");
  const blocks = [];
  let current = [];

  for (const line of lines) {
    if (isIcdDiagnosisHeadingLine(line) && current.length > 0) {
      blocks.push(current.join("\n").trim());
      current = [line];
    } else {
      current.push(line);
    }
  }

  if (current.length > 0) {
    blocks.push(current.join("\n").trim());
  }

  return blocks.filter(Boolean);
}

/**
 * Full per-diagnosis block for integrated Assessment & Plan (matches backend normalize).
 */
export function buildIntegratedDiagnosisBlock(description, code) {
  const label = formatHeadingFromDescription(description);
  const c = normalizeIcd10Code(code);
  return [
    `**${label} (${c}):**`,
    "Not documented",
    "",
    "**Plan:**",
    "- Not documented",
  ].join("\n");
}

/** Append a new diagnosis block to assessment (heading + assessment + Plan). */
export function appendAssessmentDiagnosis(assessmentText = "", description, code) {
  const block = buildIntegratedDiagnosisBlock(description, code);
  const trimmed = String(assessmentText || "").trim();
  return trimmed ? `${trimmed}\n\n${block}` : block;
}

/**
 * Append diagnosis to clinical plan when plan field uses per-diagnosis lines.
 * Leaves follow-up/signature tail unchanged.
 */
export function appendPlanDiagnosis(planText = "", description, code) {
  const normalized = String(planText || "").trim();
  if (!normalized) return "";

  const { clinicalPlan, followUp } = splitPlanFromFollowUp(normalized);
  const clinical = String(clinicalPlan || "").trim();
  const hasPerDiagnosisPlan =
    clinical.split("\n").some(isIcdDiagnosisHeadingLine) ||
    splitTextByDiagnosisHeadings(clinical).length > 1;

  if (!hasPerDiagnosisPlan) {
    return normalized;
  }

  const label = formatHeadingFromDescription(description);
  const c = normalizeIcd10Code(code);
  const block = [`- **${label} (${c}):**`, "- Not documented"].join("\n");
  const mergedClinical = clinical ? `${clinical}\n\n${block}` : block;
  return followUp ? `${mergedClinical}\n\n${followUp}` : mergedClinical;
}

/**
 * Add ICD to assessment + plan when needed. Returns { assessment, plan, changed }.
 */
export function applyIcd10CodeToSoapSections(
  assessmentText = "",
  planText = "",
  description,
  code,
) {
  const normalizedCode = normalizeIcd10Code(code);
  let assessment = String(assessmentText || "");
  let plan = String(planText || "");
  let changed = false;

  if (!extractIcdCodesFromText(assessment).includes(normalizedCode)) {
    assessment = appendAssessmentDiagnosis(assessment, description, normalizedCode);
    changed = true;
  }

  const planBefore = plan;
  plan = appendPlanDiagnosis(plan, description, normalizedCode);
  if (plan !== planBefore) changed = true;

  return { assessment, plan, changed };
}

/** Sync plan disease heading when plan uses bullet diagnosis lines. */
export function replacePlanDiagnosisHeading(
  planText = "",
  oldCode,
  newDescription,
  newCode,
  oldHeading = "",
) {
  if (!planText?.trim()) return planText;
  const oldNorm = normalizeIcd10Code(oldCode);
  const newNorm = normalizeIcd10Code(newCode);
  const newLabel = formatHeadingFromDescription(newDescription);
  const newLine = buildIcdDiagnosisHeadingLine(
    newDescription,
    newCode,
    ICD_HEADING_STYLE.ASSESSMENT_BULLET,
  );
  const escapedOld = escapeRegex(oldNorm);

  return planText
    .split("\n")
    .map((line) => {
      const isPlanHeading =
        /^\s*-\s*\*\*[^*]+\([A-Z]\d/i.test(line) && line.includes(oldNorm);
      const matchesOldHeading =
        oldHeading &&
        new RegExp(`\\*\\*\\s*${escapeRegex(oldHeading)}`, "i").test(line) &&
        line.includes(oldNorm);

      if (isPlanHeading || matchesOldHeading) {
        return newLine;
      }

      if (line.includes(oldNorm)) {
        return line
          .replace(
            new RegExp(
              `\\*\\*\\s*[^*]*?\\(\\s*${escapedOld}\\s*\\)\\s*:?\\s*\\*\\*`,
              "gi",
            ),
            `**${newLabel} (${newNorm}):**`,
          )
          .replace(
            new RegExp(
              `\\*\\*([^*]+)\\*\\*\\s*\\(\\s*${escapedOld}\\s*\\)\\s*:?`,
              "gi",
            ),
            `**${newLabel} (${newNorm}):**`,
          );
      }
      return line;
    })
    .join("\n");
}

function isDiagnosisHeadingLine(line, code) {
  const c = normalizeIcd10Code(code);
  if (!c || !line.includes(c)) return false;
  return isIcdDiagnosisHeadingLine(line);
}

/** Extract unique ICD-10 codes from free text (assessment/plan). */
export function extractIcdCodesFromText(text = "") {
  const found = new Set();
  const canonicalPattern =
    /\*\*[^*]+\(\s*([A-Z]\d{2}(?:\.\d{1,3})?)\s*\)\s*:?\s*\*\*/gi;
  let match;
  while ((match = canonicalPattern.exec(text)) !== null) {
    const code = normalizeIcd10Code(match[1]);
    if (isValidIcd10Code(code)) found.add(code);
  }
  const icdPattern =
    /(?:ICD[- ]?10[- ]?CM?[- ]?:?|\(|\[)([A-Z]\d{2}(?:\.\d{1,3})?)(?:\)|\])?/gi;
  while ((match = icdPattern.exec(text)) !== null) {
    const code = normalizeIcd10Code(match[1]);
    if (isValidIcd10Code(code)) found.add(code);
  }
  return Array.from(found);
}

/**
 * Unique ICD-10 codes in the order they first appear when reading the given
 * texts top to bottom (e.g. assessment, then plan).
 */
export function extractIcdCodesInDocumentOrder(...texts) {
  const ordered = [];
  const seen = new Set();
  texts.forEach((text) => {
    if (!text) return;
    const pattern =
      /(?:ICD[- ]?10[- ]?CM?[- ]?:?|\(|\[)([A-Z]\d{2}(?:\.\d{1,3})?)(?:\)|\])?/gi;
    let match;
    while ((match = pattern.exec(text)) !== null) {
      const code = normalizeIcd10Code(match[1]);
      if (isValidIcd10Code(code) && !seen.has(code)) {
        seen.add(code);
        ordered.push(code);
      }
    }
  });
  return ordered;
}

/**
 * Sort problem-list entries to follow the SOAP note: codes that appear in the
 * text come first in reading order; the rest (precharting/manual) keep their
 * existing relative order and follow after.
 */
export function sortProblemsBySoapOrder(problems = [], ...texts) {
  const order = extractIcdCodesInDocumentOrder(...texts);
  const rank = new Map(order.map((code, i) => [code, i]));
  return problems
    .map((problem, index) => ({ problem, index }))
    .sort((a, b) => {
      const ra = rank.get(normalizeIcd10Code(a.problem.code));
      const rb = rank.get(normalizeIcd10Code(b.problem.code));
      if (ra != null && rb != null) return ra - rb;
      if (ra != null) return -1;
      if (rb != null) return 1;
      return a.index - b.index;
    })
    .map(({ problem }) => problem);
}

/** Replace ICD-10 code and headings using ScribeAI canonical format. */
export function replaceIcdCodeAndHeadingInText(
  text = "",
  oldCode,
  newCode,
  newDescription = "",
  knownOldHeading = "",
) {
  if (!text) return text;

  const oldNorm = normalizeIcd10Code(oldCode);
  const newNorm = normalizeIcd10Code(newCode);
  const newLabel = formatHeadingFromDescription(newDescription);
  const oldHeading =
    knownOldHeading?.trim() ||
    findDiagnosisLabelForCode(text, oldNorm) ||
    "";
  const escapedOld = escapeRegex(oldNorm);

  let result = text
    .split("\n")
    .map((line) => {
      if (isDiagnosisHeadingLine(line, oldNorm)) {
        const numMatch = line.match(/^(\s*)(\d+)\.\s*/);
        if (numMatch) {
          return buildIcdDiagnosisHeadingLine(
            newDescription,
            newNorm,
            ICD_HEADING_STYLE.NUMBERED,
            { lineNumber: numMatch[2] },
          );
        }
        if (/^\s*-\s*/.test(line)) {
          return buildIcdDiagnosisHeadingLine(
            newDescription,
            newNorm,
            ICD_HEADING_STYLE.ASSESSMENT_BULLET,
          );
        }
        return buildIcdDiagnosisHeadingLine(
          newDescription,
          newNorm,
          ICD_HEADING_STYLE.INLINE_BOLD,
        );
      }

      if (!line.includes(oldNorm) && !(oldHeading && line.includes(oldHeading))) {
        return line;
      }

      let updated = line
        .replace(
          new RegExp(
            `\\*\\*\\s*[^*]*?\\(\\s*${escapedOld}\\s*\\)\\s*:?\\s*\\*\\*`,
            "gi",
          ),
          `**${newLabel} (${newNorm}):**`,
        )
        .replace(
          new RegExp(
            `\\*\\*([^*]+)\\*\\*\\s*\\(\\s*${escapedOld}\\s*\\)\\s*:?`,
            "gi",
          ),
          `**${newLabel} (${newNorm}):**`,
        )
        .replace(
          new RegExp(
            `\\*\\*\\s*${escapedOld}\\s*[–-]\\s*[^*]+\\s*\\*\\*`,
            "gi",
          ),
          `**${newLabel} (${newNorm}):**`,
        );

      if (
        oldHeading &&
        oldHeading.toLowerCase() !== newLabel.toLowerCase() &&
        /^\s*-\s*\*\*/.test(updated)
      ) {
        const escapedHeading = escapeRegex(oldHeading);
        updated = updated.replace(
          new RegExp(`\\*\\*\\s*${escapedHeading}\\s*\\(`, "i"),
          `**${newLabel} (`,
        );
      }

      return updated;
    })
    .join("\n");

  if (oldNorm !== newNorm) {
    result = result
      .replace(new RegExp(`\\(\\s*${escapedOld}\\s*\\)`, "gi"), `(${newNorm})`)
      .replace(new RegExp(`\\[\\s*${escapedOld}\\s*\\]`, "gi"), `[${newNorm}]`)
      .replace(
        new RegExp(`(ICD[- ]?10[- ]?CM?[- ]?:?\\s*)${escapedOld}\\b`, "gi"),
        `$1${newNorm}`,
      );
  }

  return result;
}

/** @deprecated Use replaceIcdCodeAndHeadingInText */
export function replaceIcdCodeInText(text = "", oldCode, newCode) {
  return replaceIcdCodeAndHeadingInText(text, oldCode, newCode, "");
}

/** Diagnosis labels from assessment text that may need ICD-10 lookup. */
export function extractDiagnosisTermsForSuggestions(assessmentText = "") {
  const terms = [];
  const seen = new Set();

  const addTerm = (raw) => {
    const term = String(raw || "")
      .replace(/\*\*/g, "")
      .replace(/\s*\([A-Z]\d{2}[^)]*\)\s*/gi, "")
      .replace(/:\s*$/, "")
      .trim();
    if (term.length < 3) return;
    const key = term.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    terms.push(term);
  };

  const canonicalPattern =
    /\*\*\s*([^*]+?)\s*\(\s*([A-Z]\d{2}(?:\.\d{1,3})?)\s*\)\s*:?\s*\*\*/gi;
  let match;
  while ((match = canonicalPattern.exec(assessmentText)) !== null) {
    addTerm(match[1]);
  }

  const numberedPattern = /(\d+)\.\s*\*\*([^*]+)\*\*/gi;
  while ((match = numberedPattern.exec(assessmentText)) !== null) {
    addTerm(match[2]);
  }

  const lines = assessmentText.split("\n");
  for (const line of lines) {
    const lineMatch = line.match(
      /^\s*[-*•]?\s*\*\*([^*]+)\*\*(?:\s*\(([A-Z]\d{2}[^)]*)\))?/i,
    );
    if (lineMatch) {
      const code = lineMatch[2] ? normalizeIcd10Code(lineMatch[2]) : "";
      if (!code || !isValidIcd10Code(code)) {
        addTerm(lineMatch[1]);
      }
    }
  }

  return terms.slice(0, 8);
}

/** Find diagnosis label tied to a specific ICD code (canonical format first). */
export function findDiagnosisLabelForCode(assessmentText = "", code) {
  const normalized = normalizeIcd10Code(code);
  if (!normalized) return "";

  const escaped = escapeRegex(normalized);

  const canonical = new RegExp(
    `\\*\\*\\s*([^*]+?)\\s*\\(\\s*${escaped}\\s*\\)\\s*:?\\s*\\*\\*`,
    "i",
  );
  const m0 = assessmentText.match(canonical);
  if (m0?.[1]) {
    return m0[1].trim().replace(/\*\*/g, "").replace(/:\s*$/, "");
  }

  const legacy = new RegExp(`\\*\\*([^*]+)\\*\\*\\s*\\(${escaped}\\)`, "i");
  const m1 = assessmentText.match(legacy);
  if (m1?.[1]) return m1[1].trim().replace(/\*\*/g, "");

  const lines = assessmentText.split("\n");
  for (const line of lines) {
    if (!line.includes(normalized)) continue;
    const inBold = line.match(
      new RegExp(`\\*\\*\\s*([^*]+?)\\s*\\(\\s*${escaped}`, "i"),
    );
    if (inBold?.[1]) return inBold[1].trim();
    const labelMatch = line.match(/\*\*([^*]+)\*\*/);
    if (labelMatch?.[1]) return labelMatch[1].trim().replace(/:\s*$/, "");
  }

  return "";
}

/** Line starts Follow-Up-Instructions or Signature (end of SOAP plan field). */
export const FOLLOW_UP_HEADING_LINE =
  /^\s*[-*•]?\s*(?:\*\*)?\s*(?:Follow[-\s]*Up(?:[-\s]*Instructions?)?|Signature)\s*:?\s*(?:\*\*)?\s*(?:.*)?$/i;

/** Remove a standalone Assessment: heading line (section title is already shown in UI). */
export function stripStandaloneAssessmentHeading(text = "") {
  return String(text || "")
    .replace(
      /^\s*[-*•]?\s*(?:\*\*)?\s*Assessment\s*:?\s*(?:\*\*)?\s*\n?/im,
      "",
    )
    .replace(
      /\n\s*[-*•]?\s*(?:\*\*)?\s*Assessment\s*:?\s*(?:\*\*)?\s*(?=\n)/gi,
      "\n",
    )
    .trim();
}

/**
 * Collapse redundant plain "Plan:" immediately before "**Plan:**"
 * (legacy notes where the model emitted Plan: and normalizer also added **Plan:**).
 */
export function collapseRedundantPlanHeadings(text = "") {
  return String(text || "")
    .replace(
      /^\s*[-*•]?\s*Plan\s*:\s*(?:\*\*)?\s*\n+(?=\s*(?:[-*•]\s*)?\*\*\s*Plan\s*:)/im,
      "",
    )
    .replace(
      /\n\s*[-*•]?\s*Plan\s*:\s*(?:\*\*)?\s*\n+(?=\s*(?:[-*•]\s*)?\*\*\s*Plan\s*:)/gi,
      "\n\n",
    )
    .trim();
}

/** Remove a standalone Plan: heading line (not per-diagnosis Plan under Assessment). */
export function stripStandalonePlanHeading(text = "") {
  return String(text || "")
    .replace(
      /^\s*[-*•]?\s*(?:\*\*)?\s*Plan\s*:?\s*(?:\*\*)?\s*\n?/im,
      "",
    )
    .replace(
      /\n\s*[-*•]?\s*(?:\*\*)?\s*Plan\s*:?\s*(?:\*\*)?\s*(?=\n)/gi,
      "\n",
    )
    .trim();
}

/**
 * Split stored plan field into clinical plan vs follow-up/signature tail.
 * Follow-up tail is rendered as returned from the backend (no frontend heading overrides).
 */
export function splitPlanFromFollowUp(planText = "") {
  const normalized = String(planText || "").replace(/\r\n/g, "\n").trim();
  if (!normalized) return { clinicalPlan: "", followUp: "" };

  const lines = normalized.split("\n");
  const splitAt = lines.findIndex((line) =>
    FOLLOW_UP_HEADING_LINE.test(line || ""),
  );

  if (splitAt === -1) {
    const inline = normalized.search(
      /\n\s*[-*•]?\s*(?:\*\*)?\s*(?:Follow[-\s]*Up(?:[-\s]*Instructions?)?|Signature)\s*:?\s*(?:\*\*)?/i,
    );
    if (inline >= 0) {
      return {
        clinicalPlan: stripStandalonePlanHeading(
          normalized.slice(0, inline).trim(),
        ),
        followUp: normalized.slice(inline).trim(),
      };
    }
    return {
      clinicalPlan: stripStandalonePlanHeading(normalized),
      followUp: "",
    };
  }

  return {
    clinicalPlan: stripStandalonePlanHeading(
      lines.slice(0, splitAt).join("\n").trim(),
    ),
    followUp: lines.slice(splitAt).join("\n").trim(),
  };
}

